import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Stage, Layer, Image as KonvaImage, Rect, Text, Group, Transformer } from 'react-konva';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export interface PdfRegionStripe {
    color: string;
    label?: string;
    weight?: number;
    taskId?: string;
}

export interface PdfRegionMarker {
    id: string;
    shape: 'circle' | 'square' | 'triangle' | 'diamond' | 'star';
    color: string;
    label?: string;
    taskId?: string;
}

export interface PdfRegion {
    id: string;
    pageNumber: number;
    xPercent: number;
    yPercent: number;
    widthPercent: number;
    heightPercent: number;
    label?: string;
    levelName?: string;
    unitName?: string;
    color?: string;
    isLocked?: boolean;
    physicalUnitId?: string;
    stripes?: PdfRegionStripe[];
    markers?: PdfRegionMarker[];
}

export interface PdfKonvaViewerHandle {
    getStage: () => any;
    toDataURL: () => string | null;
    /** Versión asíncrona que libera el hilo principal entre operaciones pesadas para evitar congelamiento */
    toDataURLAsync: () => Promise<string | null>;
    getPdfDimensions: () => { width: number; height: number };
}

interface PdfKonvaViewerProps {
    pdfUrl: string | null;
    regions: PdfRegion[];
    interactive?: boolean;
    onRegionUpdate?: (region: PdfRegion) => void;
    onRegionClick?: (region: PdfRegion | null) => void;
    onRegionHover?: (region: PdfRegion | null, e: any) => void;
    selectedRegionId?: string | null;
}

export const PdfKonvaViewer = forwardRef<PdfKonvaViewerHandle, PdfKonvaViewerProps>(({
    pdfUrl,
    regions,
    interactive = false,
    onRegionUpdate,
    onRegionClick,
    onRegionHover,
    selectedRegionId = null
}, ref) => {
    const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    
    const [pdfImage, setPdfImage] = useState<HTMLCanvasElement | null>(null);
    const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    const containerRef = useRef<HTMLDivElement>(null);
    const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
    const stageRef = useRef<any>(null);
    const trRef = useRef<any>(null);
    
    const [stageScale, setStageScale] = useState(1);
    const [stagePos, setStagePos] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setStageSize({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };
        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(updateSize);
        });
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        updateSize();
        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (!pdfUrl) {
            setPdfDoc(null);
            setPdfImage(null);
            setPdfError(null);
            return;
        }
        let isMounted = true;
        setPdfError(null);
        setIsLoading(true);
        let loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;
        
        const loadPdf = async () => {
            try {
                loadingTask = pdfjsLib.getDocument(pdfUrl);
                const pdf = await loadingTask.promise;
                if (!isMounted) return;
                setPdfDoc(pdf);
                setNumPages(pdf.numPages);
                setCurrentPage(1);
            } catch (err: any) {
                if (err.name === 'RenderingCancelledException' || err.name === 'PromiseCancelledException') return;
                console.error("Error loading PDF:", err);
                if (isMounted) setPdfError("Error al cargar el archivo PDF. Es posible que esté corrupto o el enlace sea inválido.");
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        loadPdf();
        return () => { 
            isMounted = false; 
            if (loadingTask) {
                try { loadingTask.destroy(); } catch (e) {}
            }
        };
    }, [pdfUrl]);

    useEffect(() => {
        if (!pdfDoc) return;
        let isMounted = true;
        let renderTask: pdfjsLib.RenderTask | null = null;
        
        const renderPage = async () => {
            setIsLoading(true);
            try {
                const page = await pdfDoc.getPage(currentPage);
                // Increase scale for better resolution on high DPI screens
                const viewport = page.getViewport({ scale: 2.5 }); 
                
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                if (!context) return;
                
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                
                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                    canvas: canvas
                };
                
                renderTask = page.render(renderContext);
                await renderTask.promise;
                if (!isMounted) return;
                
                setPdfImage(canvas);
                setPdfDimensions({ width: viewport.width, height: viewport.height });
                
                if (containerRef.current && stageSize.width > 0) {
                    const cw = stageSize.width;
                    const ch = stageSize.height;
                    const scale = Math.min(cw / viewport.width, ch / viewport.height) * 0.95;
                    setStageScale(scale);
                    setStagePos({
                        x: (cw - viewport.width * scale) / 2,
                        y: (ch - viewport.height * scale) / 2
                    });
                }
            } catch (err: any) {
                if (err.name === 'RenderingCancelledException') return;
                console.error("Error rendering page:", err);
                if (isMounted) setPdfError("Error al renderizar la página.");
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        renderPage();
        return () => { 
            isMounted = false; 
            if (renderTask) {
                try { renderTask.cancel(); } catch (e) {}
            }
        };
    }, [pdfDoc, currentPage, stageSize.width, stageSize.height]);

    const handleWheel = (e: any) => {
        e.evt.preventDefault();
        const scaleBy = 1.1;
        const stage = e.target.getStage();
        if (!stage) return;
        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();
        if (!pointer) return;
        
        const mousePointTo = {
            x: pointer.x / oldScale - stage.x() / oldScale,
            y: pointer.y / oldScale - stage.y() / oldScale,
        };

        const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
        
        if (newScale < 0.05 || newScale > 20) return;
        
        setStageScale(newScale);
        setStagePos({
            x: -(mousePointTo.x - pointer.x / newScale) * newScale,
            y: -(mousePointTo.y - pointer.y / newScale) * newScale,
        });
    };

    // Touch events for pinch-to-zoom
    const lastDistRef = useRef<number>(0);
    const lastCenterRef = useRef<{x: number, y: number} | null>(null);

    const getDistance = (p1: any, p2: any) => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    const getCenter = (p1: any, p2: any) => ({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });

    const handleTouchMove = (e: any) => {
        e.evt.preventDefault();
        const touch1 = e.evt.touches[0];
        const touch2 = e.evt.touches[1];

        if (touch1 && touch2) {
            const stage = e.target.getStage();
            if (!stage) return;
            if (stage.isDragging()) stage.stopDrag();

            const p1 = { x: touch1.clientX, y: touch1.clientY };
            const p2 = { x: touch2.clientX, y: touch2.clientY };

            const dist = getDistance(p1, p2);
            const center = getCenter(p1, p2);

            if (!lastDistRef.current) lastDistRef.current = dist;

            const scaleBy = dist / lastDistRef.current;
            const oldScale = stage.scaleX();
            let newScale = oldScale * scaleBy;
            
            if (newScale < 0.05) newScale = 0.05;
            if (newScale > 20) newScale = 20;

            const stageRect = stage.container().getBoundingClientRect();
            const pointer = {
                x: center.x - stageRect.left,
                y: center.y - stageRect.top,
            };

            const mousePointTo = {
                x: pointer.x / oldScale - stage.x() / oldScale,
                y: pointer.y / oldScale - stage.y() / oldScale,
            };

            setStageScale(newScale);
            setStagePos({
                x: -(mousePointTo.x - pointer.x / newScale) * newScale,
                y: -(mousePointTo.y - pointer.y / newScale) * newScale,
            });

            lastDistRef.current = dist;
            lastCenterRef.current = center;
        }
    };

    const handleTouchEnd = () => {
        lastDistRef.current = 0;
        lastCenterRef.current = null;
    };

    // Keyboard fine movements
    useEffect(() => {
        if (!interactive || !selectedRegionId) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const activeRegion = regions.find(r => r.id === selectedRegionId);
            if (!activeRegion || activeRegion.isLocked) return;

            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
                
                // Base move step. For shift, make it 10x larger
                const step = e.shiftKey ? 0.5 : 0.05; 
                let newX = activeRegion.xPercent;
                let newY = activeRegion.yPercent;

                if (e.key === 'ArrowUp') newY -= step;
                if (e.key === 'ArrowDown') newY += step;
                if (e.key === 'ArrowLeft') newX -= step;
                if (e.key === 'ArrowRight') newX += step;

                if (onRegionUpdate) {
                    onRegionUpdate({
                        ...activeRegion,
                        xPercent: newX,
                        yPercent: newY
                    });
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [interactive, selectedRegionId, regions, onRegionUpdate]);

    useEffect(() => {
        if (interactive && selectedRegionId && trRef.current && stageRef.current) {
            const node = stageRef.current.findOne(`#region-${selectedRegionId}`);
            const region = regions.find(r => r.id === selectedRegionId);
            
            if (node && region && !region.isLocked) {
                trRef.current.nodes([node]);
                trRef.current.getLayer().batchDraw();
            } else {
                trRef.current.nodes([]);
            }
        } else if (trRef.current) {
            trRef.current.nodes([]);
        }
    }, [selectedRegionId, interactive, regions, currentPage]);

    const handleDragEnd = (e: any, region: PdfRegion) => {
        if (!interactive || !onRegionUpdate) return;
        const node = e.target;
        const newX = node.x();
        const newY = node.y();
        
        onRegionUpdate({
            ...region,
            xPercent: (newX / pdfDimensions.width) * 100,
            yPercent: (newY / pdfDimensions.height) * 100
        });
    };

    const handleTransformEnd = (e: any, region: PdfRegion) => {
        if (!interactive || !onRegionUpdate) return;
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        node.scaleX(1);
        node.scaleY(1);

        onRegionUpdate({
            ...region,
            xPercent: (node.x() / pdfDimensions.width) * 100,
            yPercent: (node.y() / pdfDimensions.height) * 100,
            widthPercent: ((node.width() * scaleX) / pdfDimensions.width) * 100,
            heightPercent: ((node.height() * scaleY) / pdfDimensions.height) * 100
        });
    };

    const activeRegions = regions.filter(r => r.pageNumber === currentPage);

    const layerRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
        getStage: () => stageRef.current,
        toDataURL: () => {
            try {
                // Si tenemos el pdfImage y pdfDimensions, podemos dibujar de manera pura y nítida
                // en un canvas offscreen a resolución completa (1:1 con las dimensiones del PDF):
                if (pdfImage && pdfDimensions.width > 0 && pdfDimensions.height > 0) {
                    const offCanvas = document.createElement('canvas');
                    offCanvas.width = pdfDimensions.width;
                    offCanvas.height = pdfDimensions.height;
                    const ctx = offCanvas.getContext('2d');
                    if (ctx) {
                        // 1. Dibujar el fondo del plano PDF completo
                        ctx.drawImage(pdfImage, 0, 0, pdfDimensions.width, pdfDimensions.height);

                        // 2. Dibujar la capa vectorial de Konva a resolución nativa completa (1:1 con el PDF)
                        if (layerRef.current && stageRef.current) {
                            const stage = stageRef.current;
                            const oldScale = { x: stage.scaleX(), y: stage.scaleY() };
                            const oldPos = { x: stage.x(), y: stage.y() };

                            // Reseteamos el Stage a posición (0,0) y escala 1:1 para que ningún elemento quede cortado
                            stage.scale({ x: 1, y: 1 });
                            stage.position({ x: 0, y: 0 });

                            const layerCanvas = layerRef.current.toCanvas({
                                x: 0,
                                y: 0,
                                width: pdfDimensions.width,
                                height: pdfDimensions.height,
                                pixelRatio: 1
                            });

                            // Restauramos el zoom y paneo interactivo del usuario
                            stage.scale(oldScale);
                            stage.position(oldPos);
                            stage.batchDraw();

                            if (layerCanvas) {
                                ctx.drawImage(layerCanvas, 0, 0, pdfDimensions.width, pdfDimensions.height);
                            }
                        }

                        const resultUrl = offCanvas.toDataURL('image/png');
                        if (resultUrl && resultUrl.length > 500) {
                            return resultUrl;
                        }
                    }
                }

                // Fallback con el Layer directo
                if (layerRef.current && pdfDimensions.width > 0 && pdfDimensions.height > 0) {
                    const dataUrl = layerRef.current.toDataURL({
                        pixelRatio: 1
                    });
                    if (dataUrl && dataUrl.length > 500) {
                        return dataUrl;
                    }
                }

                // Fallback con Stage
                if (stageRef.current) {
                    return stageRef.current.toDataURL({ pixelRatio: 1 });
                }

                return null;
            } catch (err) {
                console.error("Error en toDataURL de PdfKonvaViewer:", err);
                return null;
            }
        },
        toDataURLAsync: async () => {
            // Pausa que libera el hilo principal para que el navegador procese eventos de UI
            const yieldThread = () => new Promise<void>(resolve => {
                requestAnimationFrame(() => setTimeout(resolve, 30));
            });

            try {
                if (pdfImage && pdfDimensions.width > 0 && pdfDimensions.height > 0) {
                    // Fase 1: Crear canvas offscreen
                    const offCanvas = document.createElement('canvas');
                    offCanvas.width = pdfDimensions.width;
                    offCanvas.height = pdfDimensions.height;
                    const ctx = offCanvas.getContext('2d');
                    if (!ctx) return null;

                    await yieldThread();

                    // Fase 2: Dibujar el fondo del plano PDF
                    ctx.drawImage(pdfImage, 0, 0, pdfDimensions.width, pdfDimensions.height);

                    await yieldThread();

                    // Fase 3: Dibujar la capa vectorial de Konva
                    if (layerRef.current && stageRef.current) {
                        const stage = stageRef.current;
                        const oldScale = { x: stage.scaleX(), y: stage.scaleY() };
                        const oldPos = { x: stage.x(), y: stage.y() };

                        stage.scale({ x: 1, y: 1 });
                        stage.position({ x: 0, y: 0 });

                        const layerCanvas = layerRef.current.toCanvas({
                            x: 0,
                            y: 0,
                            width: pdfDimensions.width,
                            height: pdfDimensions.height,
                            pixelRatio: 1
                        });

                        stage.scale(oldScale);
                        stage.position(oldPos);
                        stage.batchDraw();

                        await yieldThread();

                        if (layerCanvas) {
                            ctx.drawImage(layerCanvas, 0, 0, pdfDimensions.width, pdfDimensions.height);
                        }
                    }

                    await yieldThread();

                    // Fase 4: Generar data URL en JPEG (más liviano que PNG, ~60% menos bytes)
                    const resultUrl = offCanvas.toDataURL('image/jpeg', 0.92);

                    // Liberar memoria del canvas offscreen
                    offCanvas.width = 0;
                    offCanvas.height = 0;

                    if (resultUrl && resultUrl.length > 500) {
                        return resultUrl;
                    }
                }

                // Fallback con el Layer directo
                if (layerRef.current && pdfDimensions.width > 0 && pdfDimensions.height > 0) {
                    await yieldThread();
                    const dataUrl = layerRef.current.toDataURL({ pixelRatio: 1 });
                    if (dataUrl && dataUrl.length > 500) {
                        return dataUrl;
                    }
                }

                // Fallback con Stage
                if (stageRef.current) {
                    return stageRef.current.toDataURL({ pixelRatio: 1 });
                }

                return null;
            } catch (err) {
                console.error("Error en toDataURLAsync de PdfKonvaViewer:", err);
                return null;
            }
        },
        getPdfDimensions: () => pdfDimensions
    }), [pdfDimensions, pdfImage, stageSize]);

    return (
        <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-950 overflow-hidden relative" ref={containerRef}>
            {pdfUrl && numPages > 0 && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white dark:bg-slate-800 shadow-lg rounded-full px-4 py-2 flex items-center gap-4 border border-slate-200 dark:border-slate-700">
                    <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage <= 1}
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full disabled:opacity-50"
                    >
                        <span className="material-symbols-outlined text-slate-600 dark:text-slate-300">chevron_left</span>
                    </button>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Pág. {currentPage} de {numPages}
                    </span>
                    <button 
                        onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                        disabled={currentPage >= numPages}
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full disabled:opacity-50"
                    >
                        <span className="material-symbols-outlined text-slate-600 dark:text-slate-300">chevron_right</span>
                    </button>
                </div>
            )}
            
            {!pdfUrl && !pdfError && !isLoading && (
                <div className="flex items-center justify-center h-full text-slate-400">
                    <p>No hay plano seleccionado</p>
                </div>
            )}
            
            {pdfError && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/90 dark:bg-slate-900/90 text-red-500 gap-2 p-6 text-center">
                    <span className="material-symbols-outlined text-4xl">error</span>
                    <p className="font-bold">{pdfError}</p>
                </div>
            )}
            
            {isLoading && !pdfImage && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/50 dark:bg-slate-900/50 text-blue-500 gap-2">
                    <span className="material-symbols-outlined text-4xl animate-spin">refresh</span>
                    <p className="font-bold">Cargando PDF...</p>
                </div>
            )}

            {pdfUrl && stageSize.width > 0 && (
                <Stage
                    width={stageSize.width}
                    height={stageSize.height}
                    onWheel={handleWheel}
                    scaleX={stageScale}
                    scaleY={stageScale}
                    x={stagePos.x}
                    y={stagePos.y}
                    draggable
                    ref={stageRef}
                    onMouseDown={(e) => {
                        const clickedEmpty = e.target === e.target.getStage() || e.target.hasName('pdf-background');
                        if (clickedEmpty) {
                            if (onRegionClick) onRegionClick(null);
                        }
                    }}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchStart={(e) => {
                        const clickedEmpty = e.target === e.target.getStage() || e.target.hasName('pdf-background');
                        if (clickedEmpty) {
                            if (onRegionClick) onRegionClick(null);
                        }
                    }}
                >
                    <Layer ref={layerRef}>
                        {pdfImage && (
                            <KonvaImage
                                name="pdf-background"
                                image={pdfImage}
                                width={pdfDimensions.width}
                                height={pdfDimensions.height}
                            />
                        )}
                        {activeRegions.map(region => {
                            const x = (region.xPercent / 100) * pdfDimensions.width;
                            const y = (region.yPercent / 100) * pdfDimensions.height;
                            const w = (region.widthPercent / 100) * pdfDimensions.width;
                            const h = (region.heightPercent / 100) * pdfDimensions.height;
                            
                            const isSelected = selectedRegionId === region.id;
                            const hasStripes = region.stripes && region.stripes.length > 0;
                            const markers = region.markers || [];
                            
                            return (
                                <Group
                                    key={region.id}
                                    id={`region-${region.id}`}
                                    x={x}
                                    y={y}
                                    width={w}
                                    height={h}
                                    draggable={interactive && isSelected && !region.isLocked}
                                    onDragEnd={(e) => handleDragEnd(e, region)}
                                    onTransformEnd={(e) => handleTransformEnd(e, region)}
                                    onClick={() => onRegionClick?.(region)}
                                    onTap={() => onRegionClick?.(region)}
                                    onMouseEnter={(e) => {
                                        const stage = e.target.getStage();
                                        if (stage) stage.container().style.cursor = interactive ? 'pointer' : 'default';
                                        if (onRegionHover) onRegionHover(region, e);
                                    }}
                                    onMouseLeave={(e) => {
                                        const stage = e.target.getStage();
                                        if (stage) stage.container().style.cursor = 'default';
                                        if (onRegionHover) onRegionHover(null, e);
                                    }}
                                >
                                    {/* Fondo o Franjas de Superficie (según orientación y patrón de imagen 3) */}
                                    {hasStripes ? (
                                        <Group width={w} height={h} clipFunc={(ctx: any) => {
                                            const r = 4 / stageScale;
                                            ctx.beginPath();
                                            ctx.moveTo(r, 0);
                                            ctx.lineTo(w - r, 0);
                                            ctx.quadraticCurveTo(w, 0, w, r);
                                            ctx.lineTo(w, h - r);
                                            ctx.quadraticCurveTo(w, h, w - r, h);
                                            ctx.lineTo(r, h);
                                            ctx.quadraticCurveTo(0, h, 0, h - r);
                                            ctx.lineTo(0, r);
                                            ctx.quadraticCurveTo(0, 0, r, 0);
                                            ctx.closePath();
                                        }}>
                                            {(() => {
                                                const sList = region.stripes!.slice(0, 6);
                                                const count = sList.length;
                                                const isH = w >= h; // Orientación Horizontal vs Vertical

                                                if (count === 1) {
                                                    // 1: Color completo
                                                    return (
                                                        <Rect x={0} y={0} width={w} height={h} fill={sList[0].color} opacity={0.85} />
                                                    );
                                                } else if (count === 2) {
                                                    // 2: Partido a la mitad (si es H, vertical split hacia el lado; si es V, horizontal split hacia abajo)
                                                    if (isH) {
                                                        const halfW = w / 2;
                                                        return (
                                                            <>
                                                                <Rect x={0} y={0} width={halfW} height={h} fill={sList[0].color} opacity={0.85} />
                                                                <Rect x={halfW} y={0} width={w - halfW} height={h} fill={sList[1].color} opacity={0.85} />
                                                            </>
                                                        );
                                                    } else {
                                                        const halfH = h / 2;
                                                        return (
                                                            <>
                                                                <Rect x={0} y={0} width={w} height={halfH} fill={sList[0].color} opacity={0.85} />
                                                                <Rect x={0} y={halfH} width={w} height={h - halfH} fill={sList[1].color} opacity={0.85} />
                                                            </>
                                                        );
                                                    }
                                                } else if (count === 3) {
                                                    // 3: 3 franjas iguales (estilo bandera)
                                                    if (isH) {
                                                        const w3 = w / 3;
                                                        return (
                                                            <>
                                                                <Rect x={0} y={0} width={w3} height={h} fill={sList[0].color} opacity={0.85} />
                                                                <Rect x={w3} y={0} width={w3} height={h} fill={sList[1].color} opacity={0.85} />
                                                                <Rect x={w3 * 2} y={0} width={w - w3 * 2} height={h} fill={sList[2].color} opacity={0.85} />
                                                            </>
                                                        );
                                                    } else {
                                                        const h3 = h / 3;
                                                        return (
                                                            <>
                                                                <Rect x={0} y={0} width={w} height={h3} fill={sList[0].color} opacity={0.85} />
                                                                <Rect x={0} y={h3} width={w} height={h3} fill={sList[1].color} opacity={0.85} />
                                                                <Rect x={0} y={h3 * 2} width={w} height={h - h3 * 2} fill={sList[2].color} opacity={0.85} />
                                                            </>
                                                        );
                                                    }
                                                } else if (count === 4) {
                                                    // 4: 4 cuadrantes iguales (en cruz 2x2)
                                                    const halfW = w / 2;
                                                    const halfH = h / 2;
                                                    return (
                                                        <>
                                                            <Rect x={0} y={0} width={halfW} height={halfH} fill={sList[0].color} opacity={0.85} />
                                                            <Rect x={halfW} y={0} width={w - halfW} height={halfH} fill={sList[1].color} opacity={0.85} />
                                                            <Rect x={0} y={halfH} width={halfW} height={h - halfH} fill={sList[2].color} opacity={0.85} />
                                                            <Rect x={halfW} y={halfH} width={w - halfW} height={h - halfH} fill={sList[3].color} opacity={0.85} />
                                                        </>
                                                    );
                                                } else if (count === 5) {
                                                    // 5: Patrón exacto imagen 3:
                                                    // Si es H: 4 cuadros a la izquierda (2x2) + 1 franja vertical completa a la derecha
                                                    // Si es V: 4 cuadros arriba (2x2) + 1 franja horizontal completa abajo
                                                    if (isH) {
                                                        const leftW = (w * 2) / 3;
                                                        const rightW = w - leftW;
                                                        const halfH = h / 2;
                                                        const w4 = leftW / 2;
                                                        return (
                                                            <>
                                                                <Rect x={0} y={0} width={w4} height={halfH} fill={sList[0].color} opacity={0.85} />
                                                                <Rect x={w4} y={0} width={leftW - w4} height={halfH} fill={sList[1].color} opacity={0.85} />
                                                                <Rect x={0} y={halfH} width={w4} height={h - halfH} fill={sList[2].color} opacity={0.85} />
                                                                <Rect x={w4} y={halfH} width={leftW - w4} height={h - halfH} fill={sList[3].color} opacity={0.85} />
                                                                <Rect x={leftW} y={0} width={rightW} height={h} fill={sList[4].color} opacity={0.85} />
                                                            </>
                                                        );
                                                    } else {
                                                        const topH = (h * 2) / 3;
                                                        const botH = h - topH;
                                                        const halfW = w / 2;
                                                        const h4 = topH / 2;
                                                        return (
                                                            <>
                                                                <Rect x={0} y={0} width={halfW} height={h4} fill={sList[0].color} opacity={0.85} />
                                                                <Rect x={halfW} y={0} width={w - halfW} height={h4} fill={sList[1].color} opacity={0.85} />
                                                                <Rect x={0} y={h4} width={halfW} height={topH - h4} fill={sList[2].color} opacity={0.85} />
                                                                <Rect x={halfW} y={h4} width={w - halfW} height={topH - h4} fill={sList[3].color} opacity={0.85} />
                                                                <Rect x={0} y={topH} width={w} height={botH} fill={sList[4].color} opacity={0.85} />
                                                            </>
                                                        );
                                                    }
                                                } else {
                                                    // 6: 6 cuadrículas iguales (3x2 en horizontal o 2x3 en vertical)
                                                    if (isH) {
                                                        const halfH = h / 2;
                                                        const botH = h - halfH;
                                                        const w3 = w / 3;
                                                        return (
                                                            <>
                                                                <Rect x={0} y={0} width={w3} height={halfH} fill={sList[0].color} opacity={0.85} />
                                                                <Rect x={w3} y={0} width={w3} height={halfH} fill={sList[1].color} opacity={0.85} />
                                                                <Rect x={w3 * 2} y={0} width={w - w3 * 2} height={halfH} fill={sList[2].color} opacity={0.85} />
                                                                <Rect x={0} y={halfH} width={w3} height={botH} fill={sList[3].color} opacity={0.85} />
                                                                <Rect x={w3} y={halfH} width={w3} height={botH} fill={sList[4].color} opacity={0.85} />
                                                                <Rect x={w3 * 2} y={halfH} width={w - w3 * 2} height={botH} fill={sList[5].color} opacity={0.85} />
                                                            </>
                                                        );
                                                    } else {
                                                        const halfW = w / 2;
                                                        const rightW = w - halfW;
                                                        const h3 = h / 3;
                                                        return (
                                                            <>
                                                                <Rect x={0} y={0} width={halfW} height={h3} fill={sList[0].color} opacity={0.85} />
                                                                <Rect x={halfW} y={0} width={rightW} height={h3} fill={sList[1].color} opacity={0.85} />
                                                                <Rect x={0} y={h3} width={halfW} height={h3} fill={sList[2].color} opacity={0.85} />
                                                                <Rect x={halfW} y={h3} width={rightW} height={h3} fill={sList[3].color} opacity={0.85} />
                                                                <Rect x={0} y={h3 * 2} width={halfW} height={h - h3 * 2} fill={sList[4].color} opacity={0.85} />
                                                                <Rect x={halfW} y={h3 * 2} width={rightW} height={h - h3 * 2} fill={sList[5].color} opacity={0.85} />
                                                            </>
                                                        );
                                                    }
                                                }
                                            })()}
                                        </Group>
                                    ) : (
                                        <Rect
                                            width={w}
                                            height={h}
                                            fill={region.color || 'rgba(156, 163, 175, 0.4)'}
                                            opacity={0.8}
                                            cornerRadius={4 / stageScale}
                                        />
                                    )}

                                    {/* Borde del recinto */}
                                    <Rect
                                        width={w}
                                        height={h}
                                        fill="transparent"
                                        stroke={isSelected ? '#3b82f6' : (region.color || hasStripes ? 'rgba(30, 41, 59, 0.45)' : '#4b5563')}
                                        strokeWidth={isSelected ? 3 / stageScale : 1.2 / stageScale}
                                        cornerRadius={4 / stageScale}
                                    />

                                    {/* Tareas Puntuales: Celdas delimitadas con símbolos centrados (según patrón imagen 2) */}
                                    {markers.length > 0 && (
                                        <Group>
                                            {(() => {
                                                const mList = markers.slice(0, 6);
                                                const mCount = mList.length;
                                                const isH = w >= h;

                                                // Reservar espacio inferior para el texto para que jamás se traslape con los símbolos
                                                const labelAreaH = Math.min(h * 0.22, 14);
                                                const usableH = Math.max(10, h - labelAreaH);

                                                // Definir celdas geométricas para cada símbolo
                                                type Cell = { x: number; y: number; w: number; h: number };
                                                const cells: Cell[] = [];
                                                const dividerLines: { x1: number; y1: number; x2: number; y2: number }[] = [];

                                                if (mCount === 1) {
                                                    // 1 al centro
                                                    cells.push({ x: 0, y: 0, w, h: usableH });
                                                } else if (mCount === 2) {
                                                    if (isH) {
                                                        const halfW = w / 2;
                                                        cells.push({ x: 0, y: 0, w: halfW, h: usableH });
                                                        cells.push({ x: halfW, y: 0, w: w - halfW, h: usableH });
                                                        dividerLines.push({ x1: halfW, y1: 2, x2: halfW, y2: usableH - 2 });
                                                    } else {
                                                        const halfH = usableH / 2;
                                                        cells.push({ x: 0, y: 0, w, h: halfH });
                                                        cells.push({ x: 0, y: halfH, w, h: usableH - halfH });
                                                        dividerLines.push({ x1: 2, y1: halfH, x2: w - 2, y2: halfH });
                                                    }
                                                } else if (mCount === 3) {
                                                    if (isH) {
                                                        const w3 = w / 3;
                                                        cells.push({ x: 0, y: 0, w: w3, h: usableH });
                                                        cells.push({ x: w3, y: 0, w: w3, h: usableH });
                                                        cells.push({ x: w3 * 2, y: 0, w: w - w3 * 2, h: usableH });
                                                        dividerLines.push({ x1: w3, y1: 2, x2: w3, y2: usableH - 2 });
                                                        dividerLines.push({ x1: w3 * 2, y1: 2, x2: w3 * 2, y2: usableH - 2 });
                                                    } else {
                                                        const h3 = usableH / 3;
                                                        cells.push({ x: 0, y: 0, w, h: h3 });
                                                        cells.push({ x: 0, y: h3, w, h: h3 });
                                                        cells.push({ x: 0, y: h3 * 2, w, h: usableH - h3 * 2 });
                                                        dividerLines.push({ x1: 2, y1: h3, x2: w - 2, y2: h3 });
                                                        dividerLines.push({ x1: 2, y1: h3 * 2, x2: w - 2, y2: h3 * 2 });
                                                    }
                                                } else if (mCount === 4) {
                                                    const halfW = w / 2;
                                                    const halfH = usableH / 2;
                                                    cells.push({ x: 0, y: 0, w: halfW, h: halfH });
                                                    cells.push({ x: halfW, y: 0, w: w - halfW, h: halfH });
                                                    cells.push({ x: 0, y: halfH, w: halfW, h: usableH - halfH });
                                                    cells.push({ x: halfW, y: halfH, w: w - halfW, h: usableH - halfH });
                                                    dividerLines.push({ x1: halfW, y1: 2, x2: halfW, y2: usableH - 2 });
                                                    dividerLines.push({ x1: 2, y1: halfH, x2: w - 2, y2: halfH });
                                                } else if (mCount === 5) {
                                                    // Patrón imagen 2: 4 cuadrantes (2x2) a la izquierda/arriba y 1 celda grande a la derecha/abajo
                                                    if (isH) {
                                                        const leftW = (w * 2) / 3;
                                                        const rightW = w - leftW;
                                                        const halfH = usableH / 2;
                                                        const w4 = leftW / 2;
                                                        cells.push({ x: 0, y: 0, w: w4, h: halfH });
                                                        cells.push({ x: w4, y: 0, w: leftW - w4, h: halfH });
                                                        cells.push({ x: 0, y: halfH, w: w4, h: usableH - halfH });
                                                        cells.push({ x: w4, y: halfH, w: leftW - w4, h: usableH - halfH });
                                                        cells.push({ x: leftW, y: 0, w: rightW, h: usableH });

                                                        dividerLines.push({ x1: w4, y1: 2, x2: w4, y2: usableH - 2 });
                                                        dividerLines.push({ x1: 2, y1: halfH, x2: leftW, y2: halfH });
                                                        dividerLines.push({ x1: leftW, y1: 2, x2: leftW, y2: usableH - 2 });
                                                    } else {
                                                        const topH = (usableH * 2) / 3;
                                                        const botH = usableH - topH;
                                                        const halfW = w / 2;
                                                        const h4 = topH / 2;
                                                        cells.push({ x: 0, y: 0, w: halfW, h: h4 });
                                                        cells.push({ x: halfW, y: 0, w: w - halfW, h: h4 });
                                                        cells.push({ x: 0, y: h4, w: halfW, h: topH - h4 });
                                                        cells.push({ x: halfW, y: h4, w: w - halfW, h: topH - h4 });
                                                        cells.push({ x: 0, y: topH, w, h: botH });

                                                        dividerLines.push({ x1: halfW, y1: 2, x2: halfW, y2: topH });
                                                        dividerLines.push({ x1: 2, y1: h4, x2: w - 2, y2: h4 });
                                                        dividerLines.push({ x1: 2, y1: topH, x2: w - 2, y2: topH });
                                                    }
                                                } else {
                                                    // 6 celdas: 3x2 en horizontal o 2x3 en vertical
                                                    if (isH) {
                                                        const halfH = usableH / 2;
                                                        const botH = usableH - halfH;
                                                        const w3 = w / 3;
                                                        cells.push({ x: 0, y: 0, w: w3, h: halfH });
                                                        cells.push({ x: w3, y: 0, w: w3, h: halfH });
                                                        cells.push({ x: w3 * 2, y: 0, w: w - w3 * 2, h: halfH });
                                                        cells.push({ x: 0, y: halfH, w: w3, h: botH });
                                                        cells.push({ x: w3, y: halfH, w: w3, h: botH });
                                                        cells.push({ x: w3 * 2, y: halfH, w: w - w3 * 2, h: botH });

                                                        dividerLines.push({ x1: w3, y1: 2, x2: w3, y2: usableH - 2 });
                                                        dividerLines.push({ x1: w3 * 2, y1: 2, x2: w3 * 2, y2: usableH - 2 });
                                                        dividerLines.push({ x1: 2, y1: halfH, x2: w - 2, y2: halfH });
                                                    } else {
                                                        const halfW = w / 2;
                                                        const rightW = w - halfW;
                                                        const h3 = usableH / 3;
                                                        cells.push({ x: 0, y: 0, w: halfW, h: h3 });
                                                        cells.push({ x: halfW, y: 0, w: rightW, h: h3 });
                                                        cells.push({ x: 0, y: h3, w: halfW, h: h3 });
                                                        cells.push({ x: halfW, y: h3, w: rightW, h: h3 });
                                                        cells.push({ x: 0, y: h3 * 2, w: halfW, h: usableH - h3 * 2 });
                                                        cells.push({ x: halfW, y: h3 * 2, w: rightW, h: usableH - h3 * 2 });

                                                        dividerLines.push({ x1: halfW, y1: 2, x2: halfW, y2: usableH - 2 });
                                                        dividerLines.push({ x1: 2, y1: h3, x2: w - 2, y2: h3 });
                                                        dividerLines.push({ x1: 2, y1: h3 * 2, x2: w - 2, y2: h3 * 2 });
                                                    }
                                                }

                                                return (
                                                    <>
                                                        {/* Líneas divisorias sutiles entre celdas si no hay relleno de franjas */}
                                                        {!hasStripes && dividerLines.map((line, lIdx) => (
                                                            <Rect
                                                                key={`div-${lIdx}`}
                                                                x={line.x1}
                                                                y={line.y1}
                                                                width={Math.max(1, line.x2 - line.x1 || 1 / stageScale)}
                                                                height={Math.max(1, line.y2 - line.y1 || 1 / stageScale)}
                                                                fill="rgba(148, 163, 184, 0.4)"
                                                            />
                                                        ))}

                                                        {/* Dibujar cada símbolo dentro de su celda correspondiente */}
                                                        {mList.map((marker, mIdx) => {
                                                            const cell = cells[mIdx] || { x: 0, y: 0, w, h: usableH };
                                                            const cX = cell.x + cell.w / 2;
                                                            const cY = cell.y + cell.h / 2;
                                                            const s = Math.min(cell.w * 0.78, cell.h * 0.78, 48);

                                                            if (marker.shape === 'circle') {
                                                                return (
                                                                    <Rect
                                                                        key={`marker-${marker.id || mIdx}`}
                                                                        x={cX - s / 2}
                                                                        y={cY - s / 2}
                                                                        width={s}
                                                                        height={s}
                                                                        fill={marker.color}
                                                                        stroke="#ffffff"
                                                                        strokeWidth={1}
                                                                        cornerRadius={s / 2}
                                                                        shadowColor="black"
                                                                        shadowBlur={1.5}
                                                                        shadowOpacity={0.3}
                                                                    />
                                                                );
                                                            } else if (marker.shape === 'square') {
                                                                return (
                                                                    <Rect
                                                                        key={`marker-${marker.id || mIdx}`}
                                                                        x={cX - s / 2}
                                                                        y={cY - s / 2}
                                                                        width={s}
                                                                        height={s}
                                                                        fill={marker.color}
                                                                        stroke="#ffffff"
                                                                        strokeWidth={1}
                                                                        cornerRadius={1.5}
                                                                        shadowColor="black"
                                                                        shadowBlur={1.5}
                                                                        shadowOpacity={0.3}
                                                                    />
                                                                );
                                                            } else if (marker.shape === 'diamond') {
                                                                return (
                                                                    <Rect
                                                                        key={`marker-${marker.id || mIdx}`}
                                                                        x={cX}
                                                                        y={cY}
                                                                        width={s * 0.82}
                                                                        height={s * 0.82}
                                                                        rotation={45}
                                                                        offset={{ x: (s * 0.82) / 2, y: (s * 0.82) / 2 }}
                                                                        fill={marker.color}
                                                                        stroke="#ffffff"
                                                                        strokeWidth={1}
                                                                        shadowColor="black"
                                                                        shadowBlur={1.5}
                                                                        shadowOpacity={0.3}
                                                                    />
                                                                );
                                                            } else {
                                                                // Default rounded / triangle
                                                                return (
                                                                    <Rect
                                                                        key={`marker-${marker.id || mIdx}`}
                                                                        x={cX - s / 2}
                                                                        y={cY - s / 2}
                                                                        width={s}
                                                                        height={s}
                                                                        fill={marker.color}
                                                                        stroke="#ffffff"
                                                                        strokeWidth={1}
                                                                        cornerRadius={s / 3}
                                                                        shadowColor="black"
                                                                        shadowBlur={1.5}
                                                                        shadowOpacity={0.3}
                                                                    />
                                                                );
                                                            }
                                                        })}
                                                    </>
                                                );
                                            })()}
                                         </Group>
                                     )}
                                        {/* Nombre de la Unidad Física: Posicionado en la parte INFERIOR, en una sola línea compacta y nítida sin fondo */}
                                        {(region.levelName || region.unitName || region.label) && (() => {
                                             const lvl = region.levelName?.trim() || '';
                                             const un = region.unitName?.trim() || '';
                                             const singleLine = lvl && un ? `${lvl}-${un}` : (region.label || un || lvl);

                                             // Tamaño de fuente compacto y calibrado para que se vea legible sin ser gigante
                                             const fontSize = Math.max(3.8, Math.min(w * 0.08, h * 0.08, 6.5));
                                             const textHeight = fontSize + 2;
                                             const textY = Math.max(0, h - textHeight - 1);

                                             return (
                                                 <Group y={textY} width={w} height={textHeight}>
                                                     <Text
                                                         x={0}
                                                         y={0}
                                                         text={singleLine}
                                                         fill="#1e293b"
                                                         fontSize={fontSize}
                                                         fontStyle="bold"
                                                         fontFamily="Arial, sans-serif"
                                                         align="center"
                                                         verticalAlign="middle"
                                                         width={w}
                                                         height={textHeight}
                                                         wrap="none"
                                                         shadowColor="#ffffff"
                                                         shadowBlur={1}
                                                         shadowOpacity={0.8}
                                                     />
                                                 </Group>
                                             );
                                         })()}
                                </Group>
                            );
                        })}
                        {interactive && (
                            <Transformer 
                                ref={trRef} 
                                rotateEnabled={false}
                                keepRatio={false}
                                boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 1 || newBox.height < 1) return oldBox;
                                    return newBox;
                                }} 
                            />
                        )}
                    </Layer>
                </Stage>
            )}
        </div>
    );
});

PdfKonvaViewer.displayName = 'PdfKonvaViewer';
